import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Compass } from "lucide-react";

export default function LoginPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form);
      }
      toast.success("Bienvenido a VoyageMarkt");
      const to = loc.state?.from?.pathname || "/app";
      nav(to, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error de autenticación");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Image side */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1651065223139-06a933511d04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjBvY2VhbiUyMGJlYWNofGVufDB8fHx8MTc3OTM2NDg0M3ww&ixlib=rb-4.1.0&q=85"
          alt="Aerial ocean"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1128]/30 via-transparent to-[#0A1128]/70" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="text-[10px] uppercase tracking-[0.35em] mb-4 opacity-80">Editorial Cartography Studio</p>
          <h2 className="font-display text-4xl xl:text-5xl leading-tight">
            Marketing B2C para agencias de viajes que buscan vender la próxima aventura.
          </h2>
        </div>
      </div>

      {/* Form side */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-[#F9F8F6] topo-bg px-6 py-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-10">
            <Compass className="w-5 h-5 text-[#D95D39]" strokeWidth={1.5} />
            <span className="font-display text-2xl text-[#0A1128]">VoyageMarkt</span>
          </div>

          <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98] mb-3">
            {mode === "login" ? "Acceso de Agentes" : "Crear cuenta"}
          </p>
          <h1 className="font-display text-4xl text-[#0A1128] leading-none">
            {mode === "login" ? "Bienvenido de nuevo." : "Comienza tu estudio."}
          </h1>
          <p className="text-[#5C616A] mt-3 mb-8">
            {mode === "login"
              ? "Accede al panel para gestionar SEO, campañas y leads de viaje."
              : "Crea una cuenta para comenzar a diseñar campañas multicanal con IA."}
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Nombre</Label>
                  <Input
                    data-testid="register-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 bg-white border-[#E2DCD0] rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Empresa</Label>
                  <Input
                    data-testid="register-company-input"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="mt-1 bg-white border-[#E2DCD0] rounded-sm"
                  />
                </div>
              </>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Email</Label>
              <Input
                data-testid="auth-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 bg-white border-[#E2DCD0] rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Contraseña</Label>
              <Input
                data-testid="auth-password-input"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1 bg-white border-[#E2DCD0] rounded-sm"
              />
            </div>
            <Button
              data-testid="auth-submit-button"
              type="submit"
              disabled={busy}
              className="w-full bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm h-11"
            >
              {busy ? "Procesando..." : mode === "login" ? "Iniciar sesión" : "Crear cuenta"}
            </Button>
            {mode === "login" && (
              <div className="text-right -mt-1">
                <Link
                  data-testid="forgot-password-link"
                  to="/forgot-password"
                  className="text-xs text-[#8A8F98] hover:text-[#D95D39] underline-offset-4 hover:underline"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            )}
          </form>

          <div className="mt-6 text-sm text-[#5C616A]">
            {mode === "login" ? (
              <>
                ¿Aún no tienes cuenta?{" "}
                <button
                  data-testid="switch-to-register"
                  onClick={() => setMode("register")}
                  className="text-[#D95D39] underline-offset-4 hover:underline font-medium"
                >
                  Crear una
                </button>
              </>
            ) : (
              <>
                ¿Ya tienes cuenta?{" "}
                <button
                  data-testid="switch-to-login"
                  onClick={() => setMode("login")}
                  className="text-[#D95D39] underline-offset-4 hover:underline font-medium"
                >
                  Iniciar sesión
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
